import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { BillingFeature, PlanType, SourceStatusLevel } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { DoneReason } from 'webapp/enums/metric-mover';

import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import { containerDomClass, containerEventName } from 'webapp/modifiers/visible-check-container';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataManagerConnectedTableRowDatasetComponent from 'webapp/components/data-manager/connected/table-row/dataset';
import DataManagerConnectedTableRowStatusComponent from 'webapp/components/data-manager/connected/table-row/status';
import DataManagerConnectedTableRowSyncFrequencyComponent from 'webapp/components/data-manager/connected/table-row/sync-frequency';
import DummyCellComponent from 'webapp/components/dummy-cell';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import OptionsButtonComponent from 'webapp/components/options-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type Source from 'webapp/models-custom/source';
import { mapSourceToDataSource } from 'webapp/models/helpers/react-model-mapper';

import type MetricMoverService from 'webapp/services/metric-mover';
import type { SourceInfoForSyncIntervalModal } from 'webapp/services/model-manager/sources';

interface Args {
	model: Source;
	datasetsCount: number;
	shouldAutoExpandDatasets: boolean;
	isExpandedByDefault: boolean;
}

type Action =
	| 'statusObjectAction'
	| 'openDatasourceInfo'
	| 'availableMetrics'
	| 'editSettings'
	| 'reauthorizeSource'
	| 'editPermissions'
	| 'changeTimezone'
	| 'purgeSource'
	| 'deleteSource'
	| 'changeSyncInterval'
	| 'addDataset'
	| 'migrateLegacyMetrics';
export type Option = PopupListItem<Action>;

const EXPANDED_SOURCES_STORAGE_KEY = 'data-manager-expanded-sources';

function readExpandedSourceIds(): string[] {
	try {
		const ids: unknown = JSON.parse(
			sessionStorage.getItem(EXPANDED_SOURCES_STORAGE_KEY) ?? '[]',
		);
		return Array.isArray(ids) ? ids.map(String) : [];
	} catch {
		return [];
	}
}

function setSourceExpanded(id: string, isExpanded: boolean): void {
	const ids = readExpandedSourceIds().filter((storedId) => storedId !== id);
	try {
		sessionStorage.setItem(
			EXPANDED_SOURCES_STORAGE_KEY,
			JSON.stringify(isExpanded ? [...ids, id] : ids),
		);
	} catch {
		// storage blocked
	}
}

export default class DataManagerConnectedTableRowSourceComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('connect') declare connectService: Service.Connect;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('model-manager/alerts') declare mmAlertsService: Service.ModelManager.Alerts;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('data-manager') declare dataManagerService: Service.DataManager;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('metric-mover') declare metricMoverService: MetricMoverService;

	@tracked isVisibleOnScreen = false;
	@tracked showAll = false;
	@tracked expandedByUser: { isExpanded: boolean; whileAutoExpanding: boolean } | null =
		this.storedChoice;

	get model(): Source {
		return this.args.model;
	}

	get datasetsToggleText(): string {
		const total = this.args.datasetsCount;
		const matching = this.model.childSources.length;
		const text = `${total} ${total === 1 ? 'dataset' : 'datasets'}`;

		return this.args.shouldAutoExpandDatasets && matching < total
			? `${matching} of ${text}`
			: text;
	}

	get secondaryLabel(): string | undefined {
		if (this.model.isTypeIngest) {
			return this.model.subtitle;
		}
		return this.model.token ? `Legacy API - ${this.model.token}` : undefined;
	}

	get hasInlineLabel(): boolean {
		return Boolean(this.model.connection?.name || this.secondaryLabel);
	}

	get storedChoice(): { isExpanded: boolean; whileAutoExpanding: boolean } | null {
		return !this.args.shouldAutoExpandDatasets &&
			readExpandedSourceIds().includes(String(this.model.id))
			? { isExpanded: true, whileAutoExpanding: false }
			: null;
	}

	get hasMatchingDatasets(): boolean {
		return this.model.childSources.length > 0;
	}

	get isExpanded(): boolean {
		if (!this.hasMatchingDatasets) {
			return false;
		}

		const choice = this.expandedByUser;
		return choice?.whileAutoExpanding === this.args.shouldAutoExpandDatasets
			? choice.isExpanded
			: this.args.isExpandedByDefault;
	}

	get canEdit(): boolean {
		return !this.model.isRoot && !!this.model.haveBasicPermissions;
	}

	get canDelete(): boolean {
		return this.canEdit && !this.model.isLocked;
	}

	get canReauthorize(): boolean {
		return !this.model.sourceType.isWithCustomToken;
	}

	get metricsText(): string {
		const count = this.model.metrics.length;
		return `${count || 'No'} ${count === 1 ? 'metric' : 'metrics'}`;
	}

	get legacyMetricsTooltipText(): string {
		return `<strong>This data source contains legacy metrics.</strong>
			<p>Migrate them to the new dataset-based metrics.</p>
			<a role="button" class="open-migration-tool-cta" data-space-access-id="${this.model.numericId}">Open migration tool</a>`;
	}

	get optionsBtnTheme(): string {
		const firstAction = this.options[0]?.action,
			isActionUpgradePlan = this.model.statusObject.isActionUpgradePlan;

		if (firstAction === 'statusObjectAction') {
			if (isActionUpgradePlan) {
				return 'green';
			} else {
				return 'green-outlined';
			}
		} else {
			return 'blue-outlined';
		}
	}

	get isFlowActionable(): boolean {
		return (
			this.model.statusObject.canReauthorize ||
			this.model.statusObject.canReconnect ||
			this.model.statusObject.isActionSourceConnected
		);
	}

	get canCreateDataset(): boolean {
		if (this.billingService.checkIfCan(BillingFeature.ADVANCED_DATA_SOURCES)) {
			return true;
		} else if (this.billingService.checkIfCan(BillingFeature.ADVANCED_DATA_SOURCES_LIMITED)) {
			const canCreateLimitedSQLDatasets =
				this.billingService.checkIfCan(BillingFeature.SQL_LEGACY_METRICS) ||
				this.billingService.checkIfCan(BillingFeature.SQL_SOURCES);
			const canCreateLimitedGSheetsDatasets = this.billingService.checkIfCan(
				BillingFeature.GSHEETS_LEGACY_METRICS,
			);

			const sourceType = this.model.sourceType;

			if (sourceType.isSQLType && canCreateLimitedSQLDatasets) {
				return true;
			} else if (sourceType.isSheets) {
				if (this.billingService.activePlan?.planType === PlanType.STARTER) {
					return canCreateLimitedGSheetsDatasets;
				}
				return true;
			} else if (sourceType.isDatasetCustomIntegration) {
				return this.billingService.canCreateCustomIntegration;
			}

			return false;
		}

		return false;
	}

	get isSourceInErrorAndUserCantTakeAnyAction(): boolean {
		return this.isFlowActionable && !this.model.canResolveActionableStatus;
	}

	get noPossibleActionDisclaimerText(): string {
		return this.model.isConnectionPrivate
			? 'Contact connection owner to resolve the issue'
			: 'Contact connection owner or account administrator to resolve the issue';
	}

	get statusTooltipText(): string {
		return `<strong>${this.model.statusObject.title}</strong><br><p>${this.model.statusDescriptionWithLinks}</p>`;
	}

	get show15MinutesSync(): boolean {
		return (
			this.billingService.isPayingCustomer &&
			this.model.statusLevel === SourceStatusLevel.ACTIVE &&
			!this.model.isPremiumDataSync &&
			this.model.sourceType.show15MinSyncLabelPromo
		);
	}

	get options(): Option[] {
		const options: Option[] = [];

		if (
			this.model.isStatusActionable &&
			(!this.isFlowActionable || this.model.canResolveActionableStatus)
		) {
			options.push({
				class: 'attention',
				icon: 'AlertFill',
				text: this.model.statusActionCapitalized,
				action: 'statusObjectAction',
			});
		}

		options.push({
			icon: 'InfoFill',
			text: 'Details',
			action: 'openDatasourceInfo',
		});

		if (!this.model.isTypeIngest && !this.model.isAbstract) {
			options.push({
				icon: 'Metrics',
				text: 'See all metrics',
				action: 'availableMetrics',
			});
		}

		if (this.model.haveBasicPermissions) {
			if (
				!this.model.canReconnect &&
				!this.model.canReauthorize &&
				this.model.sourceType.isFileUpload
			) {
				options.push({
					icon: 'Upload',
					text: 'Re-upload file',
					action: 'editSettings',
				});
			}

			if (
				this.model.sourceType.isDatasetSupported &&
				this.canCreateDataset &&
				!this.model.isTypeIngest
			) {
				options.push({
					icon: 'Table',
					text: 'New dataset',
					action: 'addDataset',
				});
			}

			if (this.model.hasReauthenticatePermissions && !this.model.isStatusActionable) {
				options.push({
					icon: 'Reauthenticate',
					text: 'Reauthenticate',
					action: 'reauthorizeSource',
				});
			}

			if (!this.model.isRoot) {
				options.push({
					icon: 'UserAccess',
					text: 'User permissions',
					action: 'editPermissions',
					isExternal: true,
					affixIcon: 'External',
				});
			}

			if (
				!this.model.isStatusActionable &&
				!this.model.isTypeIngest &&
				!this.model.isAbstract
			) {
				options.push({
					icon: 'Refresh',
					text: 'Change sync frequency',
					action: 'changeSyncInterval',
				});
			}

			if (this.model.canEditTimezone) {
				options.push({
					icon: 'Timezone',
					text: 'Change time zone',
					action: 'changeTimezone',
				});
			}

			if (!this.model.isTypeIngest && !this.model.isAbstract) {
				options.push({
					icon: 'Purge',
					text: 'Purge historical data',
					action: 'purgeSource',
					isExternal: true,
					affixIcon: 'External',
				});
			}

			if (this.currentUserService.isAdmin && !this.model.isTypeIngest) {
				options.push(this.mmActivityLogsService.getObjectForOptionsPopup());
			}
		}

		if (this.model.hasLegacyMetricsToMigrate) {
			options.push({
				icon: 'Replace',
				text: 'Migrate legacy metrics',
				action: 'migrateLegacyMetrics',
			});
		}

		if (this.canDelete) {
			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'deleteSource',
				icon: 'Trash',
			});
		}
		return options;
	}

	get isModelAbstract(): boolean {
		return this.model.isAbstract;
	}

	get showSyncCell(): boolean {
		return !this.isModelAbstract && !this.model.isTypeIngest;
	}

	get showMetricsCell(): boolean {
		return !this.isModelAbstract;
	}

	editSourceSettings(reauthorize = false): void {
		void this.connectService.connectSourceFromIdOrKey(this.model.type, {
			sourceId: this.model.numericId,
			reauthorize,
			origin: 'connectedRow',
		});
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	openDatasourceInfo(): void {
		const dataSource = mapSourceToDataSource(
			this.model,
			this.currentUserService.numericId,
			this.accountService.id,
		);

		const billingRefreshInterval = this.billingService.features.getValue(
			BillingFeature.DATA_REFRESH_INTERVAL,
		);
		const syncInterval = this.model.customSyncInterval ?? billingRefreshInterval;

		this.accountService.handleSourceDetailsRedirect(
			this.model.id!,
			this.model.isDataset,
			dataSource,
			{
				sourceType: this.model.type,
				numericId: this.model.numericId!,
				syncIntervalData: {
					id: this.model.numericId!,
					name: this.model.title,
					createdBy: this.model.createdBy?.name || '',
					syncInterval,
					avatar: this.model.realAvatar,
					ownerId: this.model.createdById,
				},
			},
		);
	}

	@action
	availableMetrics(): void {
		const genericSourceKey = this.model.sourceType.key || 'Custom';
		this.navigationService.transitionToRoute(
			'metric-library.source.specific',
			genericSourceKey,
			this.model.id,
		);
	}

	@action
	editPermissions(): void {
		this.accountService.openAccountAppDataManagerUserPermissions(this.model.id!);
	}

	@action
	purgeSource(): void {
		this.accountService.openAccountAppDataManagerPurgeData(this.model.id!);
	}

	@action
	changeTimezone(): void {
		const dataSource = mapSourceToDataSource(
			this.model,
			this.currentUserService.numericId,
			this.accountService.id,
		);
		this.dataManagerService.changeSourceTimezone(dataSource);
	}

	@action
	deleteSource() {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (this.dataManagerService.functions) {
			const dataSource = mapSourceToDataSource(
				this.model,
				this.currentUserService.numericId,
				this.accountService.id,
			);

			this.dataManagerService.functions
				.openDataSourceDeleteDialog({
					dataSource,
					disablePortal: true,
				})
				.then(() => {
					this.mmAlertsService.savedRecords
						.filter((item) => item.dataSourceId === +this.model.id!)
						.forEach((alert) => {
							alert.deleteRecord();
						});

					void this.model.delete(true);

					this.infolayerService.ok(
						`Source <strong>${this.model.title}</strong> deleted!`,
					);
				})
				.catch((error: { title?: string } | undefined) => {
					if (error?.title && error?.title.length > 0) {
						this.datasetEventsService.handleErrorMessage(undefined, error?.title);
					} else {
						this.datasetEventsService.handleDeleteSourceError();
					}
				});
		}
	}

	@action
	editSettings(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		// same as reconnect, just without confirmation
		this.editSourceSettings();
	}

	@action
	reauthorizeSource(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const text =
				'Reauthenticating a data source can affect other data sources using the same connection. We recommend that only the connection owner or someone with equivalent access to all associated data sources performs this action.',
			source = this.model.model,
			affirmativeFunc = () => {
				this.editSourceSettings(true);
			};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: 'Are you sure you want to reauthorize data source ' + source.get('title') + '?',
			text: text,
			confirmAction: 'Reauthorize',
			cancelAction: 'Cancel',
			isWarning: true,
		});
	}

	@action
	statusObjectAction(): void {
		this.model.statusObject?.triggerAction();
	}

	@action
	changeSyncInterval(): void {
		if (!this.model.numericId || this.accountService.handleDemo()) {
			return;
		}

		const billingRefreshInterval = this.billingService.features.getValue(
			BillingFeature.DATA_REFRESH_INTERVAL,
		);
		const syncInterval = this.model.customSyncInterval ?? billingRefreshInterval;

		const dataSource: SourceInfoForSyncIntervalModal = {
			id: this.model.numericId,
			name: this.model.title,
			createdBy: this.model.createdBy?.name || '',
			syncInterval,
			avatar: this.model.realAvatar,
			ownerId: this.model.createdById,
		};

		this.mmSourcesService.openSyncIntervalModal(dataSource, 'data_manager');
	}

	@action
	openActivityLog() {
		this.mmActivityLogsService.createAndOpenPopup({
			model: this.model,
		});
	}

	@action
	toggleDatasets(event: MouseEvent): void {
		this.expandedByUser = {
			isExpanded: !this.isExpanded,
			whileAutoExpanding: this.args.shouldAutoExpandDatasets,
		};
		if (!this.args.shouldAutoExpandDatasets) {
			setSourceExpanded(String(this.model.id), this.expandedByUser.isExpanded);
		}

		// rows moved into view by a collapse have to re-check their visibility
		const container = (event.currentTarget as HTMLElement).closest(`.${containerDomClass}`);
		schedule('afterRender', () => container?.dispatchEvent(new Event(containerEventName)));
	}

	@action
	resetExpandedByUser(): void {
		if (this.expandedByUser?.whileAutoExpanding !== this.args.shouldAutoExpandDatasets) {
			this.expandedByUser = this.storedChoice;
		}
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}

	@action
	addDataset(): void {
		this.connectService.addNewDataset(this.model.sourceType.key, {
			sourceId: this.model.numericId!,
			dataSourceName: this.model.title,
		});
	}

	@action
	migrateLegacyMetrics(): void {
		this.metricMoverService.open(
			{ spaceAccessId: String(this.model.numericId) },
			{
				onDone: (reason) => {
					if (reason === DoneReason.COMPLETED) {
						this.eventManagerService.trigger(
							AppEvent.DATAMANAGER_SOURCES_CONTENT_CHANGED,
						);
					}
				},
			},
		);
	}
	<template>
		{{#if this.isVisibleOnScreen}}
			<tr
				class={{joinClasses
					(array
						"table-row"
						(if this.showAll "all-shown")
						(if this.isExpanded "datasets-expanded")
					)
				}}
				data-id={{this.model.id}}
				data-ds_type={{this.model.sourceType.key}}
				{{didUpdate this.resetExpandedByUser @shouldAutoExpandDatasets}}
			>
				<td class="c0 no-grow first">
					<img src={{this.model.realAvatar}} alt />
				</td>
				<td class="c1">
					<div class="flex">
						<EditableModelPropertyComponent
							@model={{this.model}}
							@editableProperty="title"
							@canEdit={{false}}
						/>
						{{#if this.model.hasLegacyMetricsToMigrate}}
							<span class="icon-with-tooltip">
								<CommonUiIconComponent
									@name="AlertFill"
									@size="sm"
									@class="alert"
								/>
								<ToolTipComponent
									@text={{this.legacyMetricsTooltipText}}
									@maxWidth={{320}}
									@position="top"
									@interactive={{true}}
									@extraClass="legacy-custom-tooltip"
								/>
							</span>
						{{/if}}
					</div>

					<small class={{if this.hasMatchingDatasets "with-datasets-toggler"}}>
						{{#if this.hasMatchingDatasets}}
							<a
								role="button"
								class={{joinClasses
									(array "toggler" (if this.isExpanded "toggler--expanded"))
								}}
								aria-expanded={{if this.isExpanded "true" "false"}}
								{{on "click" this.toggleDatasets}}
							>
								<CommonUiIconComponent
									@name="ChevronDown"
									@size="xs"
								/>{{this.datasetsToggleText}}
							</a>
							{{#if this.hasInlineLabel}}
								<span class="dot">&centerdot;</span>
							{{/if}}
						{{/if}}
						<span class="connection-name">
							{{#if this.model.connection.name}}
								{{highlightResult
									text=this.model.connection.name
									match=this.model.activeFilter
								}}
							{{else}}
								{{this.secondaryLabel}}
							{{/if}}
						</span>
					</small>

					{{#if this.model.connection.name}}
						{{#if this.secondaryLabel}}
							<small>{{this.secondaryLabel}}</small>
						{{/if}}
					{{/if}}
				</td>

				<td class="created prio-low" data-label="Connected: ">
					{{{this.model.createdText}}}
				</td>

				<td class="perm prio-low" data-label="Permissions: ">
					<div class={{this.model.permissionColor}}>
						<CommonUiIconComponent @name={{this.model.permissionIcon}} @size="sm" />
						<span>{{this.model.permissionText}}</span>
						{{#unless this.model.permissionsEveryone}}
							<ToolTipComponent
								@text={{this.model.permissionTooltip}}
								@position="bottom"
								@delay={{200}}
								@maxWidth={{180}}
							/>
						{{/unless}}
					</div>
				</td>

				{{#if this.model.isStatusActionable}}
					<td class="prio-medium" data-label="Sync: ">
					</td>

					<td class="c2 prio-medium" data-label="Status: ">
						<span class="status">
							<CommonUiIconComponent @name="AlertFill" @size="sm" />
							<span class="status-title">
								{{this.model.statusObject.title}}
								{{#if this.isSourceInErrorAndUserCantTakeAnyAction}}
									<ToolTipComponent
										@text={{this.noPossibleActionDisclaimerText}}
										@delay={{100}}
										@maxWidth={{200}}
									/>
								{{/if}}
							</span>
							{{#if this.model.statusDescription}}
								<ToolTipComponent
									@text={{this.statusTooltipText}}
									@maxWidth={{250}}
									@theme="light"
								/>
							{{/if}}
						</span>
					</td>
					<td class="prio-medium" data-label="">
					</td>
				{{else}}
					<td class="prio-medium" data-label="Sync: ">
						{{#if this.showSyncCell}}
							<DataManagerConnectedTableRowSyncFrequencyComponent
								@dataSource={{this.model}}
							/>
						{{/if}}
					</td>

					<td class="prio-medium" data-label="Status: ">
						<DataManagerConnectedTableRowStatusComponent @dataSource={{this.model}} />
					</td>

					<td class="metrics prio-medium" data-label="Available metrics: ">
						{{#if this.showMetricsCell}}
							{{#if this.accountService.isDemoAccount}}
								{{this.metricsText}}
							{{else}}
								<LinkTo
									class="blue-link"
									@route="metric-library.source.specific"
									@models={{array this.model.sourceType.key this.model.id}}
								>
									{{this.metricsText}}
								</LinkTo>
							{{/if}}
						{{/if}}
					</td>
				{{/if}}

				<td class="c6 button-ct last">
					<OptionsButtonComponent
						@options={{this.options}}
						@theme={{this.optionsBtnTheme}}
						@onAction={{this.doOptionsAction}}
					/>
				</td>

				<FlexibleToggleCellComponent
					@showAll={{this.showAll}}
					@toggleShowAll={{this.toggleShowAll}}
				/>

			</tr>
			{{#if this.isExpanded}}
				{{#each this.model.childSources as |childSource|}}
					<DataManagerConnectedTableRowDatasetComponent @model={{childSource}} />
				{{/each}}
			{{/if}}

		{{else}}

			<tr
				class="table-row"
				data-id={{this.model.id}}
				{{visibleCheckItem this.visibleChanged}}
			>
				<DummyCellComponent />
			</tr>

		{{/if}}
	</template>
}
